import { useContext, useEffect } from "react";
import {
  Alert,
  Badge,
  Card,
  Form,
  FormGroup,
  Modal,
  ModalBody,
} from "reactstrap";
import { Scanner } from "@yudiel/react-qr-scanner";
import { isEmpty } from "lodash";
import { useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import SiteTitle from "../../../helpers/SiteTitle/SiteTitle";
import Button from "../../../components/Button/Button";
import Icon from "../../../components/Icon/Icon";
import InfoItem from "./../../../helpers/InfoItem/InfoItem";
import ErrorMessage from "../../../helpers/ErrorMessage/ErrorMessage";
import { qrCodeScannerShopContext } from "../../../context/ShopContext/QrCodeScannerShopContext/QrCodeScannerShopContext";
import { Col, Row } from "../../../components/Grid/Grid";
import { ConvertCurrencyToText } from "../../../helpers/Currency/Currency";
import {
  convertCameraErrorToText,
  convertPaymentTypeToText,
  paymentTypeItems,
} from "../../../utils/enum";
import { modalErrorMessage } from "../../../utils/toastMessage/toastMessage";
import {
  checkQrCodeValidation,
  verifyQrCodeValidation,
} from "../../../utils/validation/validation";

const QrCodeScannerShop = () => {
  const loader = useSelector((state) => state.loader);

  const {
    user,
    token,
    setToken,
    handleReset,
    handleCheckQrCode,
    handleVerifyQrCode,
  } = useContext(qrCodeScannerShopContext);

  // Validation
  const {
    register: cRegister,
    handleSubmit: cHandleSubmit,
    reset: cReset,
    watch: cWatch,
    setValue: cSetValue,
    formState: { errors: cErrors },
  } = useForm({
    defaultValues: { isAddedValue: false },
    resolver: yupResolver(checkQrCodeValidation),
  });
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(verifyQrCodeValidation),
  });

  useEffect(() => {
    reset();
    cReset();
  }, [user, token]);

  return (
    <>
      <SiteTitle title="اسکن بارکد" />

      <Row className="justify-content-center">
        <Col size="12" sm="8" md="6" xxl="4">
          <Card body className="mb-4">
            <h4 className="text-center">بارکد مشتری را اسکن کنید</h4>
          </Card>

          <div id="scanner" className="text-center">
            <Scanner
              onScan={(result) => {
                const rawValue = result[0]?.rawValue;
                if (!!rawValue) setToken(rawValue);
              }}
              scanDelay={1000}
              onError={(err) => {
                modalErrorMessage(convertCameraErrorToText(err.name));
              }}
              paused={!!token}
            />
          </div>
        </Col>
      </Row>

      <Modal
        isOpen={!isEmpty(token)}
        toggle={handleReset}
        className="modal-dialog-centered"
        size="md"
      >
        <ModalBody>
          <span type="button" onClick={handleReset} className="close">
            <Icon name="cross-sm" />
          </span>
          <div className="p-2">
            <h5 className="title">بررسی موجودی کاربر</h5>
            <br />

            <Form
              className="is-alter"
              autoComplete="off"
              onSubmit={cHandleSubmit(handleCheckQrCode)}
            >
              <Row className="gy-4">
                <Col size="12" md="6">
                  <FormGroup>
                    <label className="form-label" htmlFor="totalAmount">
                      مبلغ کل
                    </label>
                    <div className="form-control-wrap">
                      <input
                        type="number"
                        placeholder="مبلغ کل را وارد نمایید"
                        className="form-control-lg form-control"
                        {...cRegister("totalAmount")}
                      />
                      <ErrorMessage message={cErrors.totalAmount?.message} />
                      {cWatch("totalAmount") > 0 && (
                        <p className="mt-1">
                          <ConvertCurrencyToText
                            totalAmount={cWatch("totalAmount")}
                            isCurrency
                          />
                        </p>
                      )}
                    </div>
                    {cWatch("totalAmount") > 0 && (
                      <p className="mt-1">
                        <ConvertCurrencyToText
                          price={cWatch("totalAmount")}
                          isCurrency
                        />
                      </p>
                    )}
                  </FormGroup>
                </Col>
                <Col size="12" md="6">
                  <FormGroup>
                    <label className="form-label" htmlFor="price">
                      مبلغ کسر از کیف پول
                    </label>
                    <div className="form-control-wrap">
                      <input
                        type="number"
                        placeholder="مبلغ کسر از کیف پول را وارد نمایید"
                        className="form-control-lg form-control"
                        {...cRegister("price")}
                      />
                      <ErrorMessage message={cErrors.price?.message} />
                      {cWatch("price") > 0 && (
                        <p className="mt-1">
                          <ConvertCurrencyToText
                            price={cWatch("price")}
                            isCurrency
                          />
                        </p>
                      )}
                    </div>
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <label className="form-label" htmlFor="paymentType">
                      نوع پرداخت
                    </label>
                    <div className="form-control-wrap">
                      <select
                        className="form-control-lg form-control"
                        {...cRegister("paymentType")}
                      >
                        <option value="" selected hidden>
                          نوع پرداخت را انتخاب نمایید
                        </option>
                        {paymentTypeItems.map((item) => (
                          <option
                            key={item}
                            value={item}
                            selected={item === cWatch("paymentType")}
                          >
                            {convertPaymentTypeToText(item)}
                          </option>
                        ))}
                      </select>
                      <ErrorMessage message={cErrors.paymentType?.message} />
                    </div>
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <label className="form-label" htmlFor="isAddedValue">
                      ارزش افزوده
                    </label>
                    <div className="form-control-wrap">
                      <div className="custom-control custom-switch is-right">
                        <input
                          type="checkbox"
                          className="custom-control-input"
                          checked={cWatch("isAddedValue")}
                          onChange={(e) =>
                            cSetValue("isAddedValue", e.target.checked)
                          }
                          id="isAddedValue"
                        />
                        <label
                          className="custom-control-label"
                          htmlFor="isAddedValue"
                        />
                      </div>
                      <ErrorMessage message={cErrors.isAddedValue?.message} />
                    </div>
                  </FormGroup>
                </Col>
                <Col size="12">
                  <FormGroup>
                    <label className="form-label" htmlFor="description">
                      توضیحات
                    </label>
                    <div className="form-control-wrap">
                      <textarea
                        placeholder="توضیحات را وارد نمایید"
                        className="form-control-lg form-control"
                        {...cRegister("description")}
                        rows={7}
                      />
                      <ErrorMessage message={cErrors.description?.message} />
                    </div>
                  </FormGroup>
                </Col>
                <Col size="12">
                  <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                    <li>
                      <Button
                        color="primary"
                        size="lg"
                        type="submit"
                        loading={loader}
                      >
                        بررسی
                      </Button>
                    </li>
                    <li>
                      <button
                        type="button"
                        onClick={handleReset}
                        className="link link-light"
                      >
                        انصراف
                      </button>
                    </li>
                  </ul>
                </Col>
              </Row>
            </Form>
          </div>
        </ModalBody>
      </Modal>

      <Modal
        isOpen={!isEmpty(user)}
        toggle={handleReset}
        className="modal-dialog-centered"
        size="md"
      >
        <ModalBody>
          <span type="button" onClick={handleReset} className="close">
            <Icon name="cross-sm" />
          </span>
          <div className="p-2">
            <h5 className="title">اطلاعات مشتری</h5>
            <br />
            {user.avatar && (
              <div className="d-flex justify-content-center align-items-center mb-3">
                <div
                  className="p-1 rounded-circle d-flex justify-content-center align-items-center"
                  style={{
                    width: 150,
                    height: 150,
                    border: "4px solid #ddd",
                  }}
                >
                  <img
                    className="img-fluid rounded-circle"
                    alt=""
                    src={user.avatar}
                  />
                </div>
              </div>
            )}
            <Row>
              <Col sm="6">
                <InfoItem title="نام">{user?.firstName}</InfoItem>
              </Col>
              <Col sm="6">
                <InfoItem title="نام خانوادگی">{user?.lastName}</InfoItem>
              </Col>
              <Col sm="6">
                <InfoItem title="پست الکترونیک">{user?.email}</InfoItem>
              </Col>
              <Col sm="6">
                <InfoItem title="شماره موبایل">{user?.phoneNumber}</InfoItem>
              </Col>
            </Row>
            <br />
            <Form
              className="is-alter"
              autoComplete="off"
              onSubmit={handleSubmit(handleVerifyQrCode)}
            >
              <Row className="gy-4">
                <Col size="12">
                  <Alert color="info" className="text-center mb-2">
                    کد تایید ارسال شده به شماره موبایل{" "}
                    <span className="fw-bold">{user.phoneNumber}</span> را وارد
                    کنید
                  </Alert>
                  <FormGroup>
                    <label className="form-label" htmlFor="verifyCode">
                      کد تایید
                    </label>
                    <div className="form-control-wrap">
                      <input
                        type="text"
                        placeholder="کد تایید را وارد نمایید"
                        className="form-control-lg form-control"
                        {...register("verifyCode")}
                      />
                      <ErrorMessage message={errors.verifyCode?.message} />
                    </div>
                  </FormGroup>

                  <p className="text-center">
                    زمان باقی مانده :{" "}
                    <Badge
                      id="otpCountdown"
                      className="badge-dim font-weight-bold"
                      color="primary"
                    >
                      -
                    </Badge>{" "}
                    ثانیه
                  </p>
                </Col>
                <Col size="12">
                  <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                    <li>
                      <Button
                        color="primary"
                        size="lg"
                        type="submit"
                        loading={loader}
                      >
                        تایید
                      </Button>
                    </li>
                    <li>
                      <button
                        type="button"
                        onClick={handleReset}
                        className="link link-light"
                      >
                        انصراف
                      </button>
                    </li>
                  </ul>
                </Col>
              </Row>
            </Form>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default QrCodeScannerShop;
